import type {FondPermissions, FondPermissionsDataService} from 'src/features/erp/services/fond-permissions.data-service';
import type {Observable} from 'rxjs';
import type CurrentAuthService from 'shared/services/current-auth.service';
import {BehaviorSubject, map} from 'rxjs';
import {exists} from 'shared/utils/custom-utils';

export class ErpFondPermissionsContextService {
    public static serviceName = 'erpFondPermissionsContextService';

    private readonly fondPermissions: BehaviorSubject<FondPermissions>;

    /*@ngInject*/
    constructor(private currentAuthService: CurrentAuthService,
                private fondPermissionsDataService: FondPermissionsDataService) {

        this.fondPermissions = new BehaviorSubject<FondPermissions>({showable: [], editable: []});
    }

    public setup(): void {
        this.currentAuthService.currentAuth$().subscribe((currentAuth) => {
            if(!currentAuth.evided) {
                this.fondPermissions.next({showable: [], editable: []});
                return;
            }

            this.loadUserFondPermissions();
        });
    }

    public hasShowPermission$(fondId: number): Observable<boolean> {
        return this.fondPermissions.pipe(
            map((permissions) => permissions.showable.includes(fondId))
        );
    }

    public hasEditPermission$(fondId: number): Observable<boolean> {
        return this.fondPermissions.pipe(
            map((permissions) => permissions.editable.includes(fondId))
        );
    }

    public hasShowFondPermissions(permissions: FondPermissions, fondId: number): boolean {
        return permissions.showable.includes(fondId) || permissions.editable.includes(fondId);
    }

    public hasEditFondPermissions(permissions: FondPermissions, fondId: number): boolean {
        return permissions.editable.includes(fondId);
    }

    public hasFondPermissions(permissions: FondPermissions, showFondId: number | null, editFondId: number | null): boolean {
        if (!exists(showFondId) && !exists(editFondId)) {
            return true;
        }

        if (exists(showFondId) && exists(editFondId)) {
            return this.hasShowFondPermissions(permissions, showFondId) && this.hasEditFondPermissions(permissions, editFondId);
        }

        if (exists(editFondId)) {
            return this.hasEditFondPermissions(permissions, editFondId);
        }

        if (exists(showFondId)) {
            return this.hasShowFondPermissions(permissions, showFondId);
        }

        return false;
    }

    public getFondPermissionsValue(): FondPermissions {
        return this.fondPermissions.getValue();
    }

    public get fondPermissions$(): Observable<FondPermissions> {
        return this.fondPermissions.asObservable();
    }

    private async loadUserFondPermissions(): Promise<void> {
        try {
            const fondPermissions = await this.fondPermissionsDataService.getUserFondPermissions();
            this.fondPermissions.next(fondPermissions);
        } catch {
            this.fondPermissions.next({showable: [], editable: []});
        }
    }
}